/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import type { ComponentProps } from 'react';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { StyledDropdownListItem as DefaultListItem } from './styled-components';

// Accessibility helpers for the Select caret-affordance strategy. These mirror
// the (battle-tested) phone-input CountryPicker helpers but are scoped to the
// `caretAffordance` opt-in so the default Select — used by thousands of
// consumers — keeps its existing focus/scroll behavior untouched.
//
// They solve three coupled problems with the BaseUI menu's virtual-focus model
// when a screen-reader user opens the dropdown via the caret button:
//   1. arrow keys not moving the highlight after an affordance open,
//   2. iOS VoiceOver snapping the scroll position back to the top on each swipe,
//   3. (in the component) two pickers being open at once.

// --- Session-highlight context ----------------------------------------------
//
// Carries a stable ref from the Select down to every caret DropdownListItem
// WITHOUT going through the overrides/mergeOverrides/deepMerge chain. BaseUI's
// deepMerge treats any plain object ({ current: ... }) as cloneable, so passing
// a React ref as an override prop silently produces a frozen copy whose .current
// never sees future mutations. React context is the correct vehicle for
// reactive, imperative data that must not be serialised: the portalled dropdown
// is still in the same React tree, so context propagates across the portal.
export const SelectCaretSessionContext = React.createContext<React.MutableRefObject<boolean>>({
  current: false
});

// --- Per-document pointer-move tracking ------------------------------------
//
// Suppress hover-highlight that wasn't caused by a genuine pointer move.
//
// The menu highlights options on `mouseenter`. But a `mouseenter` also fires
// when the LIST SCROLLS under a stationary cursor — on open (centering the
// selected row), when the browser scrolls a freshly focused option into view,
// and while VoiceOver navigates. Each such scroll would otherwise produce a
// spurious highlight change that scrolls again: an infinite ping-pong that
// manifests as the scroll position locking to the top.
//
// Fix: only honor a `mouseenter` if the user actually moved the pointer since we
// last consumed one. A genuine hover is always preceded by a `mousemove`; a
// scroll-induced `mouseenter` is not. Tracking is scoped to the DOCUMENT that
// owns the select's DOM (iframe-safe) and refcounted across concurrent selects.
type PointerTracking = {
  count: number;
  moved: boolean;
  onPointerMove: () => void;
};
const pointerTrackingByDocument = new WeakMap<Document, PointerTracking>();
function markPointerMoved(doc: Document) {
  const tracking = pointerTrackingByDocument.get(doc);
  if (tracking) {
    tracking.moved = true;
  }
}
export function consumePointerMoved(doc: Document): boolean {
  const tracking = pointerTrackingByDocument.get(doc);
  if (!tracking) {
    return false;
  }
  const moved = tracking.moved;
  tracking.moved = false;
  return moved;
}

// Refcounted install of the per-document pointer listeners. Returns a cleanup
// that detaches them once the last caret select in that document unmounts.
export function registerPointerTracking(doc: Document): () => void {
  let tracking = pointerTrackingByDocument.get(doc);
  if (!tracking) {
    const entry: PointerTracking = {
      count: 0,
      moved: false,
      onPointerMove: () => markPointerMoved(doc)
    };
    doc.addEventListener('mousemove', entry.onPointerMove, true);
    doc.addEventListener('pointermove', entry.onPointerMove, true);
    pointerTrackingByDocument.set(doc, entry);
    tracking = entry;
  }
  tracking.count += 1;
  return () => {
    const current = pointerTrackingByDocument.get(doc);
    if (!current) {
      return;
    }
    current.count -= 1;
    if (current.count <= 0) {
      doc.removeEventListener('mousemove', current.onPointerMove, true);
      doc.removeEventListener('pointermove', current.onPointerMove, true);
      pointerTrackingByDocument.delete(doc);
    }
  };
}

// --- macOS VoiceOver select quirk ------------------------------------------
//
// macOS VoiceOver only routes its activation gesture (double-tap / VO+Space
// press) to a listbox option whose `aria-selected="true"`. This is an
// engine-level accessibility behavior, NOT browser-branding specific:
//   • WebKit (Safari): documented — VO won't interact with an option unless it's
//     aria-selected (webkit.org/show_bug.cgi?id=231724, 167671; select2#4349).
//   • Gecko (Firefox) on macOS: VO activation clicks aren't registered on custom
//     controls (bugzilla.mozilla.org/show_bug.cgi?id=1413307).
//   • Blink (Chrome/Edge/Brave/Opera): works correctly — does NOT need this.
// In a single-select listbox only the chosen option is aria-selected, so VO
// can't activate any OTHER option it navigates to — and the option the VO cursor
// is reading isn't observable via DOM (VO cursor ≠ DOM focus), so we can't target
// it. The only reliable fix is to mark EVERY option selected, but that breaks
// single-select semantics for screen readers that DON'T need it (NVDA/JAWS would
// announce every option as "selected"). So we scope it to the affected engines:
// macOS desktop + non-Blink (WebKit/Gecko). iOS/iPadOS is excluded — its
// double-tap already dispatches real events, and iPadOS reports as "Macintosh"
// (distinguished via touch points).
const IS_MAC_VOICEOVER_SELECT_QUIRK = (() => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  const isMac = /Macintosh|Mac OS X/i.test(ua);
  const isBlink = /chrome|chromium|crios|edg|opr/i.test(ua);
  const isTouch = (navigator.maxTouchPoints ?? 0) > 1; // iPadOS masquerades as Mac
  return isMac && !isBlink && !isTouch;
})();

// --- Roving-tabindex option ------------------------------------------------

type CaretDropdownListItemProps = ComponentProps<typeof DefaultListItem> & {
  $isHighlighted?: boolean;
  tabIndex?: number;
  // Passed by the menu's getRequiredItemProps (renamed to $isKeyboardFocused by
  // menu.tsx). True for arrow keys / type-ahead, false for mouse hover and
  // click. Gates DOM-focus movement: we move focus only for keyboard navigation
  // so VoiceOver follows the highlight, but mouse hover does NOT move focus
  // (which would cause scroll-position fights near the dropdown edges).
  $isKeyboardFocused?: boolean;
};

// The BaseUI menu communicates the "current" option to assistive tech via
// `aria-activedescendant` on the focused listbox container — a virtual-focus
// model. iOS VoiceOver has no support for `aria-activedescendant` on a focused
// container (it rewinds to the first child on every swipe). So we ALSO move real
// DOM focus to the highlighted option (WAI-ARIA roving-tabindex): focus lives on
// the option, never the container, so iOS VO can't rewind, while keyboard, Mac
// VO, and Android TalkBack all advance correctly.
export const CaretAffordanceDropdownListItem = React.forwardRef<HTMLLIElement, CaretDropdownListItemProps>((props, forwardedRef) => {
  // Strip the menu's hard-coded `tabIndex={0}`; roving tabindex is derived from
  // `$isHighlighted` below. Also intercept `onMouseEnter` (the menu's
  // hover-to-highlight) so scroll-induced enters are ignored.
  const {
    children,
    $isHighlighted,
    tabIndex: _ignoredTabIndex,
    onMouseEnter: menuOnMouseEnter,
    style: menuStyle,
    'aria-selected': menuAriaSelected,
    $isKeyboardFocused,
    ...rest
  } = (props as CaretDropdownListItemProps & {
    'aria-selected'?: boolean | 'true' | 'false';
  });
  const localRef = useRef<HTMLLIElement | null>(null);
  // Session ref via context — avoids the mergeOverrides/deepMerge chain which
  // would clone a plain-object ref and freeze its .current at the initial value.
  const sessionHighlightMovedRef = useContext(SelectCaretSessionContext);

  // Only forward a hover-highlight when the pointer actually moved. A
  // scroll-induced `mouseenter` (list moving under a stationary cursor) is
  // dropped, which is what breaks the highlight ping-pong.
  const handleMouseEnter = useCallback((event: React.MouseEvent<HTMLLIElement>) => {
    const ownerDoc = event.currentTarget.ownerDocument ?? document;
    if (consumePointerMoved(ownerDoc) && typeof menuOnMouseEnter === 'function') {
      (menuOnMouseEnter as (e: React.MouseEvent<HTMLLIElement>) => void)(event);
    }
  }, [menuOnMouseEnter]);
  const setRefs = useCallback((node: HTMLLIElement | null) => {
    localRef.current = node;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<HTMLLIElement | null>).current = node;
    }
  }, [forwardedRef]);
  useEffect(() => {
    const node = localRef.current;
    if ($isHighlighted && node) {
      const ownerDoc = node.ownerDocument;
      // Move DOM focus to the newly-highlighted option when focus is already
      // inside the listbox (affordance-open mode). This is the mechanism that
      // makes arrow-key navigation work with VoiceOver/TalkBack: DOM focus
      // follows the highlight so the AT announces each newly focused option.
      // When focus is outside the listbox, the check is false and focus stays
      // put — aria-activedescendant drives the AT there.
      //
      // IMPORTANT: only move focus for keyboard navigation. The menu sets
      // isKeyboardFocused=false for mouse-hover changes, and moving focus on
      // every hover causes focus thrashing near the dropdown edges, competing
      // with browser scroll recalculation and producing visible scroll jumps.
      //
      // `preventScroll`: the menu's scrollItemIntoView handles scrolling
      // gently, so focus() must not also scroll (it would fight the menu's
      // scroll and could bubble up to scroll the page).
      const listbox = node.closest('[role="listbox"]');
      if ($isKeyboardFocused && listbox?.contains(ownerDoc.activeElement) && ownerDoc.activeElement !== node) {
        node.focus({
          preventScroll: true
        });
      }

      // Center the selected option on the INITIAL open only. The menu's own
      // componentDidMount scroll runs before the listbox layout/refs are ready
      // on first open and leaves the list scrolled to the top, so we re-do it
      // here on the next frame (after layout) to land on the selected option.
      // During navigation we do NOT scroll: the menu's scrollItemIntoView keeps
      // the highlighted option visible gently. Gating on the session-moved ref
      // hands navigation back to that gentle scroll.
      const isInitialOpenHighlight = !sessionHighlightMovedRef.current;
      if (isInitialOpenHighlight) {
        const view = ownerDoc.defaultView ?? window;
        view.requestAnimationFrame(() => {
          if (typeof localRef.current?.scrollIntoView === 'function') {
            localRef.current.scrollIntoView({
              block: 'center'
            });
          }
        });
      }
    }
  }, [$isHighlighted, $isKeyboardFocused, sessionHighlightMovedRef]);
  return <DefaultListItem ref={setRefs} tabIndex={$isHighlighted ? 0 : -1} $isHighlighted={$isHighlighted} onMouseEnter={handleMouseEnter} style={{
    cursor: 'pointer',
    ...(menuStyle as React.CSSProperties)
  }} {...rest}
  // macOS VoiceOver (WebKit/Gecko): mark every option selected so the
  // activation gesture is routed to whichever option the VO cursor is on
  // (see IS_MAC_VOICEOVER_SELECT_QUIRK). Other browsers/screen readers keep
  // true single-select semantics (only the chosen option is selected).
  aria-selected={IS_MAC_VOICEOVER_SELECT_QUIRK ? true : menuAriaSelected}
  // Stable marker for the ACTUALLY-chosen option, independent of the macOS
  // VoiceOver aria-selected override above. The on-open focus/centering logic
  // keys off this so it still lands on the selected option even when every
  // option reports aria-selected=true.
  data-baseui-selected={menuAriaSelected === true || menuAriaSelected === 'true' ? 'true' : undefined}>
      {children}
    </DefaultListItem>;
});
CaretAffordanceDropdownListItem.displayName = 'CaretAffordanceDropdownListItem';

// --- On-open focus loop -----------------------------------------------------

// Resolve the listbox's selected option, preferring the REAL chosen option
// (`data-baseui-selected`) over `aria-selected` — which is forced true on every
// option under the macOS VoiceOver quirk and would otherwise resolve to the
// first option — and finally falling back to the first option.
function findSelectedOption(listbox: HTMLElement | null): HTMLElement | null {
  return listbox?.querySelector<HTMLElement>('[role="option"][data-baseui-selected="true"]') ?? listbox?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ?? listbox?.querySelector<HTMLElement>('[role="option"]') ?? null;
}

// Move DOM focus onto the selected option after a caret-affordance-driven open.
// Uses a bounded requestAnimationFrame loop because the menu's own mount-focus
// can reclaim focus for a few frames right after the dropdown opens.
//
// ownerDoc: the document that owns the select's DOM (accounts for iframe
// embeddings where the global `document` may be the parent realm's).
export function focusSelectedOptionAfterAffordanceOpen(ownerDoc: Document, listboxId: string): void {
  const view = ownerDoc.defaultView ?? window;
  let attempts = 0;
  const focusSelectedOption = () => {
    const listbox = ownerDoc.getElementById(listboxId);
    const option = findSelectedOption(listbox);
    if (option && ownerDoc.activeElement === option) {
      return;
    }
    // preventScroll: the menu's own scrollItemIntoView handles centering, so we
    // must not let focus() also scroll (it would fight that logic and can bubble
    // up to scroll the page).
    option?.focus({
      preventScroll: true
    });
    if (attempts++ < 20) {
      view.requestAnimationFrame(focusSelectedOption);
    }
  };
  view.requestAnimationFrame(focusSelectedOption);
}

// Move real DOM focus onto the currently-active listbox option. Used when a
// type-to-search caret Select hands off from the (aria-hidden) input to the
// listbox on the first Arrow key: while focus sits on the input the menu only
// tracks the highlight virtually (aria-activedescendant), which never reaches
// assistive tech, so on Arrow we pull focus onto the real option.
//
// Prefers the highlighted option (the menu's `aria-activedescendant` target),
// falling back to the selected / first option. Deferred one frame so it runs
// after the re-render that re-points keyboardControlNode at the listbox.
export function focusActiveListboxOption(ownerDoc: Document, listboxId: string, activeDescendantId: string | null | undefined): void {
  const view = ownerDoc.defaultView ?? window;
  view.requestAnimationFrame(() => {
    const listbox = ownerDoc.getElementById(listboxId);
    if (!listbox) {
      return;
    }
    const active = activeDescendantId ? ownerDoc.getElementById(activeDescendantId) : null;
    const option = active ?? findSelectedOption(listbox);
    if (option && ownerDoc.activeElement !== option) {
      // preventScroll: the menu's scrollItemIntoView handles centering.
      option.focus({
        preventScroll: true
      });
    }
  });
}

// --- Tab dismissal: close + advance focus to the adjacent field --------------

// Selector for elements that can hold focus, used to find the field adjacent to
// the caret button in the tab order so Tab / Shift+Tab from inside the open
// listbox both dismisses it AND advances focus to the next / previous field
// (rather than parking focus on the caret and forcing a second Tab).
const FOCUSABLE_SELECTOR = ['a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[contenteditable="true"]', '[tabindex]'].join(',');

// Returns the focusable element immediately before/after `from` in document (tab)
// order. Skips negative-tabindex nodes (e.g. the listbox container), aria-hidden
// subtrees (e.g. the caret-mode combobox input), and anything with no layout box.
// The portalled listbox renders at the end of the document, so the forward
// neighbor of the caret is the following form field even while the dropdown is
// still tearing down.
export function getAdjacentTabbable(from: HTMLElement | null, forward: boolean): HTMLElement | null {
  if (!from) {
    return null;
  }
  // Query the document that actually owns the caret, not the global one: in an
  // iframe embedding the picker's JS may run in the parent realm while its DOM
  // lives in the iframe document, so the global `document` would be the wrong tab
  // ring and `indexOf(from)` would never match.
  const ownerDoc = from.ownerDocument;
  const tabbables = Array.from(ownerDoc.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(el => {
    if (el.tabIndex < 0) {
      return false;
    }
    if (el.getAttribute('aria-hidden') === 'true' || el.closest('[aria-hidden="true"]')) {
      return false;
    }
    // No layout box → not visible (also filters jsdom, where focus advancement is
    // covered by the browser integration tests instead).
    return el.getClientRects().length > 0;
  });
  const index = tabbables.indexOf(from);
  if (index === -1) {
    return null;
  }
  return tabbables[forward ? index + 1 : index - 1] ?? null;
}

// Restore focus when a caret-affordance dropdown closes.
//
// Where focus should land:
//   - normally the caret button (so the AT cursor stays in the field instead of
//     falling to <body> or the next focusable element);
//   - but when closing via Tab / Shift+Tab, the adjacent field in the tab order
//     so one Tab both dismisses the dropdown and advances focus. Falls back to
//     the caret when there is no adjacent field.
//
// We restore in two phases (mirroring the phone-input country picker), because
// the Popover/Layer teardown on close can drop focus a few frames later:
//   1. Sync + next animation frame, unconditionally: covers the common close
//      paths (Esc, selection, Tab advance).
//   2. A few later passes, but ONLY if focus was "effectively lost": it landed on
//      <body>, a detached node, or got pulled back inside our own (aria-hidden)
//      Select control. We never yank focus the user has since moved to a real,
//      connected element elsewhere.
export function scheduleCaretCloseFocusRestore(params: {
  caretRef: React.MutableRefObject<HTMLElement | null>;
  tabDirection: 'forward' | 'backward' | null;
}): void {
  const {
    caretRef,
    tabDirection
  } = params;
  const getOwnerDoc = (): Document => caretRef.current?.ownerDocument ?? document;
  const view = getOwnerDoc().defaultView ?? window;
  const getFocusTarget = (): HTMLElement | null => {
    if (tabDirection) {
      const adjacent = getAdjacentTabbable(caretRef.current, tabDirection === 'forward');
      if (adjacent) {
        return adjacent;
      }
    }
    return caretRef.current;
  };
  const restoreFocus = () => {
    const target = getFocusTarget();
    if (target?.isConnected) {
      target.focus();
    }
  };
  const restoreFocusIfLost = () => {
    const ownerDoc = getOwnerDoc();
    const target = getFocusTarget();
    if (!target?.isConnected) {
      return;
    }
    const active = (ownerDoc.activeElement as HTMLElement | null);
    if (active === target) {
      return;
    }
    // Focus stranded back inside our own Select (e.g. the teardown pulled it onto
    // the aria-hidden combobox input or the collapsed caret) counts as lost when
    // we meant to advance away from the field.
    const selectRoot = caretRef.current?.closest<HTMLElement>('[data-baseweb="select"]') ?? null;
    const stuckInOwnField = active != null && selectRoot?.contains(active) === true;
    const lost = active === null || active === ownerDoc.body || active.isConnected === false || stuckInOwnField;
    if (lost) {
      target.focus();
    }
  };
  restoreFocus();
  view.requestAnimationFrame(restoreFocus);
  [150, 300, 450].forEach(delay => view.setTimeout(restoreFocusIfLost, delay));
}